import { useRef } from "react";
import { useState } from "react";
import { useEffect } from "react";
import Document from "./Document";
import { Document as DocumentType } from "../types";
import Title from "./ui/Title";
import Button from "./ui/Button";
import { IconLoader2 } from "@tabler/icons-react";

const Documents = ({ documents, getDocumentsFromCharacteristic, autoUpdate, setAutoUpdate, loading }: { documents: DocumentType[], getDocumentsFromCharacteristic: () => void, autoUpdate: boolean, setAutoUpdate: React.Dispatch<React.SetStateAction<boolean>>, loading: boolean }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    
    const scrollDocuments = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            // Scroll by the width of 2 documents (2 * 20vw)
            const scrollDistance = window.innerWidth * 0.4; // 2 * 20vw = 40vw = 0.4 of viewport width
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollDistance : scrollDistance,
                behavior: 'smooth'
            });
        }
    };

    // Check if scrolling is possible
    const checkScrollability = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            // Can scroll left if not at the beginning
            setCanScrollLeft(scrollLeft > 0);
            // Can scroll right if not at the end
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5); // 5px buffer for rounding
        }
    };

    // Check scrollability on mount and when documents change
    useEffect(() => {
        checkScrollability();
        // Set initial scroll state after documents render
        setTimeout(checkScrollability, 100);
    }, [documents]);

    // Add scroll event listener
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', checkScrollability);
            window.addEventListener('resize', checkScrollability);
            
            return () => {
                scrollContainer.removeEventListener('scroll', checkScrollability);
                window.removeEventListener('resize', checkScrollability);
            };
        }
    }, []);

    return (
        <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2 items-center h-[60vh] lg:h-[70vh]">
            <div className="flex flex-row justify-between gap-4 w-full">
                <div className="flex flex-row gap-2 items-center">
                    <Title title="Documents" />
                    {loading && <IconLoader2 className="w-4 h-4 animate-spin" />}
                </div>
                <div className="flex flex-row gap-4 items-center">
                    <input 
                        type="checkbox" 
                    checked={autoUpdate} 
                    onChange={() => setAutoUpdate(!autoUpdate)} 
                    className="w-5 h-5 cursor-pointer"
                />
                    <label className="cursor-pointer">Auto Update</label>
                </div>
            </div>
            <Button 
                onClick={getDocumentsFromCharacteristic}
            >
                Get Documents
            </Button>

            <div className="flex flex-row items-center w-[45vw] relative">
                {documents.length > 0 && canScrollLeft && (
                    <button 
                        onClick={() => scrollDocuments('left')} 
                        className="absolute left-0 z-10 bg-white/80 hover:bg-white shadow-md rounded-full p-2 transform -translate-x-1/2"
                        aria-label="Scroll left"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M15 6l-6 6l6 6" />
                        </svg>
                    </button>
                )}
                
                <div 
                    ref={scrollContainerRef} 
                    className="flex flex-row gap-4 w-[45vw] overflow-x-scroll scrollbar-hide"
                >
                    {documents.map(document => (
                        <Document 
                            key={document.document.id} 
                            document={document.document} 
                            similarity_score={document.similarity_score} 
                            metadata={document.metadata} 
                            document_name={document.document_name}
                        />
                    ))}
                </div>
                
                {documents.length > 0 && canScrollRight && (
                    <button 
                        onClick={() => scrollDocuments('right')} 
                        className="absolute right-0 z-10 bg-white/80 hover:bg-white shadow-md rounded-full p-2 transform translate-x-1/2"
                        aria-label="Scroll right"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M9 6l6 6l-6 6" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
};

export default Documents;